/**
 * Renders an agent's declared input_schema as real form fields.
 *
 * Replaces the blank JSON textarea that used to be the only way to give an
 * agent its input. That box required the user to already know the exact key
 * names — which existed nowhere in the UI, only as prose inside the agent's
 * system prompt.
 *
 * Anything the renderer does not understand falls back to the JSON editor
 * rather than showing a broken or half-empty form. See `isRenderableSchema`.
 */

import { Plus, X } from 'lucide-react';
import { useCallback } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  emptyValueFor,
  humanise,
  type InputSchema,
  type InputSchemaNode,
} from '@/types/inputSchema';

interface SchemaFormProps {
  schema: InputSchema;
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  disabled?: boolean;
}

/**
 * Relative width of a column in a repeatable row.
 *
 * Equal halves wasted the space: a company name is ~12 characters and a board
 * API URL is ~60, so the name sat in a half-empty box while the URL was cut off
 * mid-domain. Weighted by what the field actually holds, not by count.
 */
function columnWeight(key: string, node: InputSchemaNode): number {
  // 3, not 2: a Greenhouse board URL is ~60 characters. At an even split it was
  // cut off mid-domain ("…greenhouse.io/v1/boa") while "Grafana Labs" used a
  // quarter of its box. This puts the name at 25% and the URL at 75%.
  if (node.format === 'uri' || /url|link|endpoint|href|webhook/i.test(key)) return 3;
  return 1;
}

/** Long free text gets a textarea; a one-line answer gets an input. */
function wantsTextarea(node: InputSchemaNode, key: string): boolean {
  if (node.format === 'textarea') return true;
  return /summary|description|body|notes|prompt|instructions/i.test(key);
}

function FieldLabel({ node, name, htmlFor }: { node: InputSchemaNode; name: string; htmlFor: string }) {
  return (
    <div className="space-y-1">
      <Label htmlFor={htmlFor}>{node.title ?? humanise(name)}</Label>
      {node.description && (
        <p className="text-xs text-muted-foreground">{node.description}</p>
      )}
    </div>
  );
}

/** A list of plain strings — locations, tags, keywords. Rendered as chips. */
function StringListField({
  node,
  name,
  value,
  onChange,
  disabled,
}: {
  node: InputSchemaNode;
  name: string;
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
}) {
  const items = Array.isArray(value) ? value : [];

  return (
    <div className="space-y-2">
      <FieldLabel node={node} name={name} htmlFor={`f-${name}`} />
      <div className="flex flex-wrap gap-2">
        {items.map((item, i) => (
          <span
            key={i}
            className="inline-flex items-center gap-1 rounded-md border bg-muted/40 px-2 py-1 text-sm"
          >
            <input
              aria-label={`${node.title ?? humanise(name)} ${i + 1}`}
              className="w-auto min-w-[6rem] bg-transparent outline-none"
              value={item}
              disabled={disabled}
              size={Math.max(item.length, 6)}
              onChange={(e) => {
                const next = [...items];
                next[i] = e.target.value;
                onChange(next);
              }}
            />
            <button
              type="button"
              aria-label={`Remove ${item || 'entry'}`}
              disabled={disabled}
              className="text-muted-foreground hover:text-foreground"
              onClick={() => onChange(items.filter((_, j) => j !== i))}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() => onChange([...items, ''])}
        >
          <Plus className="mr-1 h-3 w-3" />
          Add
        </Button>
      </div>
    </div>
  );
}

/** A list of objects — the repeatable rows, e.g. company + board URL. */
function ObjectListField({
  node,
  name,
  value,
  onChange,
  disabled,
}: {
  node: InputSchemaNode;
  name: string;
  value: Record<string, unknown>[];
  onChange: (next: Record<string, unknown>[]) => void;
  disabled?: boolean;
}) {
  const rows = Array.isArray(value) ? value : [];
  const itemProps = Object.entries(node.items?.properties ?? {});
  const label = node.title ?? humanise(name);

  return (
    <div className="space-y-2">
      <FieldLabel node={node} name={name} htmlFor={`f-${name}`} />
      <div className="space-y-2">
        {rows.map((row, i) => (
          <div key={i} className="flex items-end gap-2">
            <div
              className="schema-row grid flex-1 gap-2"
              style={
                {
                  '--schema-cols': itemProps
                    .map(([k, n]) => `${columnWeight(k, n)}fr`)
                    .join(' '),
                } as React.CSSProperties
              }
            >
              {itemProps.map(([childKey, childNode]) => (
                <div key={childKey} className="space-y-1">
                  <Label
                    htmlFor={`f-${name}-${i}-${childKey}`}
                    className="text-xs text-muted-foreground"
                  >
                    {childNode.title ?? humanise(childKey)}
                  </Label>
                  <Input
                    id={`f-${name}-${i}-${childKey}`}
                    value={String(row?.[childKey] ?? '')}
                    disabled={disabled}
                    placeholder={
                      childNode.examples?.[0] !== undefined
                        ? String(childNode.examples[0])
                        : undefined
                    }
                    onChange={(e) => {
                      const next = [...rows];
                      next[i] = { ...next[i], [childKey]: e.target.value };
                      onChange(next);
                    }}
                  />
                </div>
              ))}
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              disabled={disabled || rows.length === 1}
              aria-label={`Remove ${label} row ${i + 1}`}
              onClick={() => onChange(rows.filter((_, j) => j !== i))}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={disabled}
        onClick={() => onChange([...rows, emptyValueFor(node.items ?? {}) as Record<string, unknown>])}
      >
        <Plus className="mr-1 h-3 w-3" />
        Add {label.toLowerCase().replace(/^which /, '')}
      </Button>
    </div>
  );
}

function ScalarField({
  node,
  name,
  value,
  onChange,
  disabled,
}: {
  node: InputSchemaNode;
  name: string;
  value: unknown;
  onChange: (next: unknown) => void;
  disabled?: boolean;
}) {
  const id = `f-${name}`;
  const placeholder =
    node.examples?.[0] !== undefined ? String(node.examples[0]) : undefined;

  if (node.type === 'boolean') {
    return (
      <label className="flex items-center gap-2 text-sm" htmlFor={id}>
        <input
          id={id}
          type="checkbox"
          className="h-4 w-4"
          checked={Boolean(value)}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span>{node.title ?? humanise(name)}</span>
      </label>
    );
  }

  if (node.enum?.length) {
    return (
      <div className="space-y-2">
        <FieldLabel node={node} name={name} htmlFor={id} />
        <select
          id={id}
          className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
          value={String(value ?? '')}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">Select…</option>
          {node.enum.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <FieldLabel node={node} name={name} htmlFor={id} />
      {wantsTextarea(node, name) ? (
        <Textarea
          id={id}
          rows={4}
          value={String(value ?? '')}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <Input
          id={id}
          type={node.type === 'number' || node.type === 'integer' ? 'number' : 'text'}
          value={String(value ?? '')}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(e) =>
            onChange(
              node.type === 'number' || node.type === 'integer'
                ? e.target.value === ''
                  ? ''
                  : Number(e.target.value)
                : e.target.value
            )
          }
        />
      )}
    </div>
  );
}

function NodeField({
  node,
  name,
  value,
  onChange,
  disabled,
}: {
  node: InputSchemaNode;
  name: string;
  value: unknown;
  onChange: (next: unknown) => void;
  disabled?: boolean;
}) {
  if (node.type === 'array') {
    return node.items?.type === 'object' ? (
      <ObjectListField
        node={node}
        name={name}
        value={value as Record<string, unknown>[]}
        onChange={onChange as (n: Record<string, unknown>[]) => void}
        disabled={disabled}
      />
    ) : (
      <StringListField
        node={node}
        name={name}
        value={value as string[]}
        onChange={onChange as (n: string[]) => void}
        disabled={disabled}
      />
    );
  }

  if (node.type === 'object' && node.properties) {
    const obj = (value ?? {}) as Record<string, unknown>;
    return (
      <fieldset className="space-y-4 rounded-lg border p-4">
        <legend className="px-1 text-sm font-medium">
          {node.title ?? humanise(name)}
        </legend>
        {node.description && (
          <p className="-mt-2 text-xs text-muted-foreground">{node.description}</p>
        )}
        {Object.entries(node.properties).map(([childKey, childNode]) => (
          <NodeField
            key={childKey}
            node={childNode}
            name={childKey}
            value={obj[childKey]}
            disabled={disabled}
            onChange={(next) => onChange({ ...obj, [childKey]: next })}
          />
        ))}
      </fieldset>
    );
  }

  return (
    <ScalarField
      node={node}
      name={name}
      value={value}
      onChange={onChange}
      disabled={disabled}
    />
  );
}

export function SchemaForm({ schema, value, onChange, disabled }: SchemaFormProps) {
  const setField = useCallback(
    (key: string, next: unknown) => onChange({ ...value, [key]: next }),
    [value, onChange]
  );

  return (
    <div className="space-y-5">
      {Object.entries(schema.properties ?? {}).map(([key, node]) => (
        <NodeField
          key={key}
          node={node}
          name={key}
          value={value?.[key]}
          disabled={disabled}
          onChange={(next) => setField(key, next)}
        />
      ))}
    </div>
  );
}
